import React, { useCallback } from "react";

import { Text } from "@skydio/rivet-ui";

import ConfirmModal from "components/common/ConfirmModal";
import { useUnclaimDevice } from "hooks/mutation/unclaimDevice";
import { useUnlinkDeviceFromOrganization } from "hooks/mutation/unlinkDeviceFromOrganization";
import { displayMutationResults } from "utils/mutations";
import { trackUserAction } from "utils/trackEvent";

import type { FC } from "react";

// This defines how we want Relay to handle the device state once the device </> org
// is unlinked. DELETE will delete the device from Relay's state, this is used in
// the Settings -> Devices page (customer view). Use REFRESH if you want to preserve
// the device but update it's metadata/state in Relay, this is used in the Admin views.
export enum UnclaimBehaviour {
  DELETE = "delete",
  REFRESH = "refresh",
}

interface Props {
  displayName: string;
  visible: boolean;
  deviceNodeId: string;
  connectionIds: Array<string>;
  onClose: () => void;
  mode?: UnclaimBehaviour;
}
const additionalInfo = (
  <Text>This will stop linking any future flights with this device to your organization.</Text>
);

const ConfirmUnclaimDeviceModal: FC<Props> = ({
  displayName,
  visible,
  onClose,
  deviceNodeId,
  connectionIds,
  mode,
}) => {
  const [unclaimDevice] = useUnclaimDevice(
    displayMutationResults(response => response.unclaimDevice, {
      successMessage: () => `Successfully unclaimed ${displayName}`,
    })
  );

  const [unlinkDevice] = useUnlinkDeviceFromOrganization(
    displayMutationResults(response => response.unlinkDeviceFromOrganization, {
      successMessage: () => `Successfully unclaimed ${displayName}`,
    })
  );

  const handleUnclaim = useCallback(() => {
    if (mode === UnclaimBehaviour.REFRESH) {
      unlinkDevice(deviceNodeId);
    } else {
      unclaimDevice(deviceNodeId, connectionIds);
    }
    onClose();
    trackUserAction("unclaim_device", { displayName: displayName });
  }, [mode, deviceNodeId, connectionIds, unclaimDevice, unlinkDevice, onClose, displayName]);

  return (
    <ConfirmModal
      confirmTitleText={`Are you sure you want to unclaim the device '${displayName}' from your organization?`}
      additionalInfo={additionalInfo}
      okText="Yes"
      cancelText="No"
      visible={visible}
      onClose={onClose}
      onOk={handleUnclaim}
      warning
    />
  );
};

export default ConfirmUnclaimDeviceModal;
